import { useEffect, useRef } from 'react'
import { SideoverState } from '@/states'
import { useNotification } from '@/hooks'

const useCapture = () => {
    const { Form, SetState, Loading, Open } = SideoverState()
    const { InitNotification } = useNotification()

    const OpenRef = useRef(Open)
    const LoadingRef = useRef(Loading)
    const FormRef = useRef(Form)
    const isClipboardError = useRef(false)

    useEffect(() => {
        OpenRef.current = Open
    }, [Open])

    useEffect(() => {
        LoadingRef.current = Loading
    }, [Loading])

    useEffect(() => {
        FormRef.current = Form
    }, [Form])

    const HandleCapture = async (event: ClipboardEvent) => {
        const activeElementTag = document.activeElement?.tagName
        const isInput = activeElementTag === 'INPUT' || activeElementTag === 'TEXTAREA' || activeElementTag === 'SELECT'

        if (OpenRef.current && !LoadingRef.current && !isInput) {
            const clipboardData = event.clipboardData

            isClipboardError.current = true

            if (clipboardData) {
                const items = clipboardData.items

                for (let i = 0; i < items.length; i++) {
                    if (items[i].type === 'application/json') {
                        const s = await new Promise<string>((resolve) => {
                            items[i].getAsString(resolve)
                        })

                        try {
                            const json = JSON.parse(s)

                            if (json?.type === '@webflow/XscpData') {
                                const form = structuredClone(FormRef.current)

                                form.Clipboard = json
                                isClipboardError.current = false

                                SetState({ Form: form })
                                InitNotification('Webflow Element was captured successfully and is ready to be saved.')
                            }

                            else InitNotification('Your clipboard does\'t contain a valid Webflow Component!')
                        }

                        catch (err) {
                            InitNotification('Something went wrong while trying to capture your Webflow component from clipboard!')
                        }
                    }
                }

                if (isClipboardError.current) InitNotification('Your clipboard does\'t contain a valid Webflow Component!')
            }
        }
    }

    const HandleRemove = async (event: KeyboardEvent) => {
        const activeElementTag = document.activeElement?.tagName
        const isInput = activeElementTag === 'INPUT' || activeElementTag === 'TEXTAREA' || activeElementTag === 'SELECT'

        if ((event.ctrlKey || event.metaKey) && event.key === 'x' && Open && !LoadingRef.current && !isInput && FormRef?.current?.Clipboard !== '') {
            const form = structuredClone(FormRef.current)

            form.Clipboard = ''
            SetState({ Form: form })
        }
    }

    useEffect(() => {
        document.addEventListener('paste', HandleCapture)
        return () => document.removeEventListener('paste', HandleCapture)
    }, [])

    useEffect(() => {
        window.addEventListener('keydown', HandleRemove)
        return () => window.removeEventListener('keydown', HandleRemove)
    }, [Open])
}

export default useCapture
